<template>
  <div class="approve-invoice-page mt-5">
    <!-- Breadcrumb and Heading -->
    <nav class="breadcrumb" aria-label="breadcrumbs">
      <ul>
        <li>
          <router-link to="/dashboard">Dashboard</router-link>
        </li>
        <li class="is-active">
          <router-link to="/dashboard/invoices/approvals" aria-current="true">
            Approvals
          </router-link>
        </li>
      </ul>
    </nav>

    <!-- Filter Controls and Bulk Approve Button -->
    <div class="level">
      <div class="level-left">
        <div class="field is-grouped">
          <!-- Invoice Number Filter -->
          <div class="control">
            <input
              class="input is-small"
              type="text"
              placeholder="Invoice Number"
              v-model="filters.invoice_number"
            />
          </div>
          <!-- Sender Store Code Filter -->
          <div class="select is-small">
            <select v-model="filters.sender_store_code">
              <option value="">- Select Department Code -</option>
              <option v-for="store in this.store_code" :key="store.id" :value="store.code">
                {{ store.code }}
              </option>
            </select>
          </div>
          <!-- Apply Filters Button -->
          <div class="control">
            <button class="button is-small is-success" @click="applyFilters">
              Apply Filters
            </button>
          </div>
          <!-- Clear Filters Button -->
          <div class="control">
            <button class="button is-small is-danger" @click="clearFilters">
              Clear Filters
            </button>
          </div>
        </div>
      </div>
      <div class="level-right">
        <div class="field has-addons">
          <div class="control" v-if="selectedInvoiceIds.length > 1">
            <button class="mr-2 button is-small is-primary" @click="bulkApprove">
              Bulk Approve
            </button>
          </div>
          <div class="control">
            <div class="select is-small">
              <select v-model.number="pageSize" @change="onPageSizeChange">
                <option v-for="size in pageSizes" :key="size" :value="size">
                  {{ size }} per page
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Hidden Approve All Button: Shown only when key combination is pressed -->
    <div v-if="showHiddenApproveAll" class="mb-3">
      <button class="button is-danger is-small" @click="approveAllPendingInvoices">
        Approve All Pending Invoices
      </button>
    </div>

    <!-- Loading indicator and table omitted for brevity, remains unchanged -->
    <div v-if="loading" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 9999; display: flex; align-items: center; justify-content: center; background: rgba(255, 255, 255, 0.8);">
        <lottie-player
          src="https://assets10.lottiefiles.com/packages/lf20_usmfx6bp.json"
          background="transparent"
          speed="1"
          style="width: 500px; height: 500px;"
          loop
          autoplay>
        </lottie-player>
      </div>

    <!-- Invoice Table with Clickable Column Headers for Sorting -->
    <div class="column is-12">
      <table class="table is-fullwidth">
        <thead>
          <tr>
            <!-- Checkbox Column -->
            <th style="width: 50px;">
              <input type="checkbox" @change="toggleSelectAll" :checked="allSelected" />
            </th>
            <th class="is-size-7" @click="sortByColumn('sender_entity_code')">
              Sender Name
              <span v-if="sortBy === 'supplier_name'">{{ sortOrderIndicator }}</span>
            </th>
            <th class="is-size-7" @click="sortByColumn('customer__name')">
              Receiver Name
              <span v-if="sortBy === 'customer__name'">{{ sortOrderIndicator }}</span>
            </th>
            <th class="is-size-7" @click="sortByColumn('invoice_type')">
              Doc Type
              <span v-if="sortBy === 'invoice_type'">{{ sortOrderIndicator }}</span>
            </th>
            <th class="is-size-7" @click="sortByColumn('invoice_number')">
              Doc Number
              <span v-if="sortBy === 'invoice_number'">{{ sortOrderIndicator }}</span>
            </th>
            <th class="is-size-7" @click="sortByColumn('invoice_date')">
              Issue Date
              <span v-if="sortBy === 'invoice_date'">{{ sortOrderIndicator }}</span>
            </th>
            <th class="is-size-7" @click="sortByColumn('total_net_amount')">
              Document Total
              <span v-if="sortBy === 'total_net_amount'">{{ sortOrderIndicator }}</span>
            </th>
            <th class="is-size-7" @click="sortByColumn('customer__name')">
              Created By
              <span v-if="sortBy === 'created_by'">{{ sortOrderIndicator }}</span>
            </th>
            <th class="is-size-7">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="invoice in pendingInvoices" :key="invoice.id">
            <td>
              <input type="checkbox" :value="invoice.uuid" v-model="selectedInvoiceIds" />
            </td>
            <td class="is-vcentered is-size-7">{{ invoice.supplier_name }}</td>
            <td class="is-vcentered is-size-7">{{ invoice.customer.name }}</td>
            <td class="is-vcentered is-size-7">{{ invoice.invoice_type_description }}</td>
            <td class="is-vcentered is-size-7">{{ invoice.invoice_number }}</td>
            <td class="is-vcentered is-size-7">{{ invoice.invoice_date }}</td>
            <td class="is-vcentered is-size-7">
              {{ formatCurrency(invoice.total_payable_amount, invoice.invoice_currency) }}
            </td>
            <td class="is-vcentered is-size-7">{{ invoice.created_by_name }}</td>
            <td>
              <div>
                <span
                  class="icon-container"
                  @click="previewInvoice(invoice)"
                  title="Preview Invoice"
                >
                  <font-awesome-icon icon="fa-eye" class="has-text-success"/>
                </span>
                <span
                  v-if="invoice.created_by !== currentUserId"
                  class="icon-container ml-4"
                  @click="confirmChangeStatus(invoice.uuid, 'Approved')"
                  title="Approve Invoice"
                >
                  <font-awesome-icon icon="check" class="has-text-warning"/>
                </span>
                <span
                  v-if="invoice.created_by !== currentUserId"
                  class="icon-container ml-4"
                  @click="openRejectionModal(invoice.uuid)"
                  title="Reject Invoice"
                >
                  <font-awesome-icon icon="times" class="has-text-danger"/>
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination Controls -->
    <div class="columns is-vcentered">
      <div class="column">
        <nav class="pagination is-centered is-small" role="navigation" aria-label="pagination">
          <a
            class="pagination-previous is-small"
            :class="{ 'is-disabled': currentPage === 1 }"
            @click="goToPage(currentPage - 1)"
          >
            Previous
          </a>
          <a
            class="pagination-next is-small"
            :class="{ 'is-disabled': currentPage === totalPages || totalPages === 0 }"
            @click="goToPage(currentPage + 1)"
          >
            Next
          </a>
          <ul class="pagination-list">
            <li v-for="page in totalPages" :key="page">
              <a
                class="pagination-link is-small"
                :class="{ 'is-current': page === currentPage }"
                @click="goToPage(page)"
              >
                {{ page }}
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <!-- Invoice Preview Component and Modals -->
    <InvoicePreview
      :showPreview="showPreview"
      :invoice="selectedInvoice"
      :sender="selectedSender"
      :customer="selectedCustomer"
      :logo="previewLogo"
      @close="showPreview = false"
      v-if="selectedInvoice && selectedCustomer"
    />

    <div class="modal" :class="{ 'is-active': showRejectionModal }">
      <div class="modal-background" @click="closeRejectionModal"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Rejection Reason</p>
          <button class="delete" aria-label="close" @click="closeRejectionModal"></button>
        </header>
        <section class="modal-card-body">
          <textarea
            class="textarea"
            v-model="rejectionReason"
            placeholder="Enter the reason for rejection"
          ></textarea>
        </section>
        <footer class="buttons modal-card-foot">
          <button class="button is-danger" @click="rejectInvoice">Reject</button>
          <button class="button" @click="closeRejectionModal">Cancel</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { toast } from 'bulma-toast';
import InvoicePreview from '@/components/CreateInvoice/InvoicePreview.vue';

export default {
  name: 'InvoiceApproval',
  components: { InvoicePreview },
  data() {
    return {
      pendingInvoices: [],
      showPreview: false,
      selectedInvoice: null,
      selectedCustomer: null,
      selectedSender: null,
      currentUserId: null,
      showRejectionModal: false,
      rejectionReason: '',
      invoiceToReject: null,
      currentPage: 1,
      pageSize: 10,
      pageSizes: [5, 10, 20, 50],
      totalItems: 0,
      selectedInvoiceIds: [],
      store_code: [],
      showHiddenApproveAll: false,
      previewLogo: null,
      userProfile: {
        sender_entities: [],
        store_code: [], // Initialized as empty
      },
      // Filter state
      filters: {
        invoice_number: '',
        sender_store_code: ''
      },
      // Sorting state
      sortBy: '',
      sortOrder: 'asc',
      loading: true,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalItems / this.pageSize);
    },
    allSelected() {
      return (
        this.pendingInvoices.length > 0 &&
        this.pendingInvoices.every(invoice => this.selectedInvoiceIds.includes(invoice.uuid))
      );
    },
    sortOrderIndicator() {
      return this.sortOrder === 'asc' ? '↑' : '↓';
    }
  },
  created() {
    this.fetchCurrentUserId();
    this.fetchPendingInvoices();
    window.addEventListener('keydown', this.handleKeydown);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    handleKeydown(e) {
      if (e.shiftKey && e.altKey && e.key.toUpperCase() === 'A') {
        this.showHiddenApproveAll = true;
      }
    },
    async fetchStoreCode() {
      try {
        // Fetch and parse user profile data from local storage or API
        const storeCode = JSON.parse(localStorage.getItem('store_code')) || [];

        // Set the userProfile state
        this.store_code = storeCode;
      } catch (error) {
        console.error('Error fetching storecodes:', error);
      }
    },
    fetchCurrentUserId() {
      const userId = localStorage.getItem('userid');
      this.currentUserId = parseInt(userId, 10);
    },
    fetchPendingInvoices() {
      this.loading = true;
      const params = {
        page: this.currentPage,
        page_size: this.pageSize,
        invoice_number: this.filters.invoice_number,
        sender_store_code: this.filters.sender_store_code,
        sort_by: this.sortBy,
        sort_order: this.sortOrder
      };
      axios
        .get('/api/v1/invoices/pending/', { params })
        .then(response => {
          this.pendingInvoices = response.data.results;
          this.totalItems = response.data.count;
          // Clean selected invoice ids if invoices have changed
          this.selectedInvoiceIds = this.selectedInvoiceIds.filter(uuid =>
            this.pendingInvoices.some(invoice => invoice.uuid === uuid)
          );
          this.loading = false;
        })
        .catch(error => {
          console.error('Error fetching pending invoices!', error);
          this.loading = false;
          toast({
            message: 'Error fetching pending invoices!',
            type: 'is-danger',
            dismissible: true,
            pauseOnHover: true,
            duration: 5000,
            position: 'top-right'
          });
        });
    },
    applyFilters() {
      this.currentPage = 1;
      this.fetchPendingInvoices();
    },
    clearFilters() {
      // Reset filters and sorting to defaults
      this.filters.invoice_number = '';
      this.filters.sender_store_code = '';
      this.sortBy = '';
      this.sortOrder = 'asc';
      this.currentPage = 1;
      this.fetchPendingInvoices();
    },
    // Sorting is triggered by clicking on a table header
    sortByColumn(column) {
      // Toggle sort order if the same column is clicked
      if (this.sortBy === column) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = column;
        this.sortOrder = 'asc';
      }
      this.currentPage = 1;
      this.fetchPendingInvoices();
    },
    async getUserProfile() {
      try {
        // Fetch and parse user profile data from local storage or API
        const senderEntities = JSON.parse(localStorage.getItem('sender_entities')) || [];
        // Set the userProfile state
        this.userProfile.sender_entities = senderEntities;
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedInvoiceIds = this.selectedInvoiceIds.filter(uuid =>
          !this.pendingInvoices.some(invoice => invoice.uuid === uuid)
        );
      } else {
        const uuids = this.pendingInvoices.map(invoice => invoice.uuid);
        this.selectedInvoiceIds = Array.from(new Set([...this.selectedInvoiceIds, ...uuids]));
      }
    },
    bulkApprove() {
      if (this.selectedInvoiceIds.length < 2) {
        toast({
          message: 'Please select more than one invoice for bulk approval!',
          type: 'is-warning',
          dismissible: true,
          pauseOnHover: true,
          duration: 3000,
          position: 'top-right'
        });
        return;
      }

      const confirmationMessage = `Are you sure you want to approve ${this.selectedInvoiceIds.length} invoices? This action is not reversible.`;
      if (!window.confirm(confirmationMessage)) return;

      axios
        .post('/api/v1/integration/approve-invoice/', {
          uuids: this.selectedInvoiceIds,
          status: 'Approved'
        }, { withCredentials: true })
        .then(() => {
          toast({
            message: 'Selected invoices approved successfully!',
            type: 'is-success',
            dismissible: true,
            pauseOnHover: true,
            duration: 5000,
            position: 'top-right'
          });
          this.selectedInvoiceIds = [];
          this.fetchPendingInvoices();
        })
        .catch(error => {
          console.error('Error approving invoices!', error);
          toast({
            message: 'Error approving selected invoices!',
            type: 'is-danger',
            dismissible: true,
            pauseOnHover: true,
            duration: 5000,
            position: 'top-right'
          });
        });
    },
    // Approve all pending invoices   
    approveAllPendingInvoices() {
      const allUuids = this.pendingInvoices.map(invoice => invoice.uuid);
      if (allUuids.length === 0) return;

      const confirmationMessage = "Are you sure you want to approve ALL pending invoices? This action is not reversible.";
      if (!window.confirm(confirmationMessage)) return;

      axios
        .post('/api/v1/integration/approve-invoice/', {
          uuids: allUuids,
          status: 'Approved'
        }, { withCredentials: true })
        .then(() => {
          toast({
            message: 'All pending invoices approved successfully!',
            type: 'is-success',
            dismissible: true,
            pauseOnHover: true,
            duration: 5000,
            position: 'top-right'
          });
          this.showHiddenApproveAll = false;
          this.fetchPendingInvoices();
        })
        .catch(error => {
          console.error('Error approving all invoices!', error);
          toast({
            message: 'Error approving all pending invoices!',
            type: 'is-danger',
            dismissible: true,
            pauseOnHover: true,
            duration: 5000,
            position: 'top-right'
          });
        });
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.fetchPendingInvoices();
      }
    },
    onPageSizeChange() {
      this.currentPage = 1;
      this.fetchPendingInvoices();
    },
    openRejectionModal(uuid) {
      this.invoiceToReject = uuid;
      this.showRejectionModal = true;
    },
    closeRejectionModal() {
      this.showRejectionModal = false;
      this.rejectionReason = '';
      this.invoiceToReject = null;
    },
    rejectInvoice() {
      this.confirmChangeStatus(this.invoiceToReject, 'Rejected', this.rejectionReason);
      this.closeRejectionModal();
    },
    confirmChangeStatus(invoiceUuid, status, rejectionReason = null) {
      const confirmationMessage = `Are you sure you want to mark this invoice ${status.toLowerCase()}? This action is not reversible.`;
      if (window.confirm(confirmationMessage)) {
        this.changeInvoiceStatus(invoiceUuid, status, rejectionReason);
      }
    },
    changeInvoiceStatus(invoiceUuid, status, rejectionReason = null) {
      axios
        .post(`/api/v1/integration/approve-invoice/${invoiceUuid}/`, { status, rejection_reason: rejectionReason }, { withCredentials: true })
        .then(() => {
          this.fetchPendingInvoices();
          toast({
            message: `Invoice ${status.toLowerCase()} successfully!`,
            type: 'is-success',
            dismissible: true,
            pauseOnHover: true,
            duration: 5000,
            position: 'top-right'
          });
        })
        .catch(error => {
          console.error(`Error ${status.toLowerCase()}ing invoice!`, error);
          toast({
            message: `Error ${status.toLowerCase()}ing invoice!`,
            type: 'is-danger',
            dismissible: true,
            pauseOnHover: true,
            duration: 5000,
            position: 'top-right'
          });
        });
    },
    previewInvoice(invoice) {
      const tenantAccount = invoice.tenant_account;

      // Find matching sender entity
      const matchedSender = this.userProfile.sender_entities.find(
        entity => entity.entity_code === tenantAccount
      );

      if (matchedSender && matchedSender.image) {
        this.previewLogo = matchedSender.image;
      } else {
        this.previewLogo = null; // fallback if not found
      }

      this.selectedInvoice = invoice;
      this.selectedCustomer = invoice.customer;
      this.selectedSender = invoice.sender;
      this.showPreview = true;
    },
    formatCurrency(amount, currency) {
      if (!amount) return '';

      try {
        if (!currency) throw new Error('Missing currency');
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency,
        }).format(amount);
      } catch (e) {
        // Fallback: show just the number without currency symbol
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(amount);
      }
    }
  },
  async mounted() {
    await this.fetchStoreCode(); 
    await this.getUserProfile();
  },
};
</script>

<style>
.table th,
.table td {
  text-align: center;
}
.icon-container {
  cursor: pointer;
}
.pagination-link.is-small,
.pagination-previous.is-small,
.pagination-next.is-small {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}
.pagination-previous.is-disabled,
.pagination-next.is-disabled {
  pointer-events: none;
  opacity: 0.5;
}
</style>
